/**
 * Human Persian labels for the operations console (owner review §36, §38). Raw codes stay available as
 * secondary/technical detail; they are never the primary label. Unknown codes fall back to the code.
 */
export const KYC1_STATUS: Record<string, string> = {
  NOT_STARTED: 'شروع نشده',
  IN_PROGRESS: 'شروع نشده',
  CHECKING: 'در حال بررسی',
  VERIFIED: 'تأییدشده',
  NEEDS_CORRECTION: 'نیازمند اصلاح',
  TEMPORARILY_UNAVAILABLE: 'خطای سرویس استعلام',
  REJECTED: 'ردشده',
  PENDING: 'در انتظار بررسی',
};

export const ACCOUNT_STATUS: Record<string, string> = {
  ACTIVE: 'فعال',
  SUSPENDED: 'تعلیق‌شده',
  LOCKED: 'قفل',
  BLOCKED: 'مسدود',
};

export const LEVEL_LABEL: Record<string, string> = { NONE: 'سطح ۰', KYC1: 'سطح ۱', KYC2: 'سطح ۲' };

export const SOURCE_LABEL = (s: string | null | undefined): string =>
  s === 'ADMIN_OVERRIDE'
    ? 'تصمیم مدیر'
    : s === 'SYSTEM'
      ? 'استعلام خودکار'
      : s === 'PROVIDER'
        ? 'استعلام بانکی'
        : '—';

export const REASON_LABEL: Record<string, string> = {
  IDENTITY_MOBILE_MISMATCH: 'موبایل به نام این کد ملی نیست',
  IDENTITY_DETAILS_MISMATCH: 'نام یا تاریخ تولد مطابقت ندارد',
  IDENTITY_MISMATCH: 'اطلاعات هویتی مطابقت ندارد',
  IDENTITY_CONFLICT: 'کد ملی برای حساب دیگری تأیید شده',
  IDENTITY_PROVIDER_UNAVAILABLE: 'سرویس استعلام موقتاً در دسترس نیست',
  PROVIDER_UNAVAILABLE: 'سرویس استعلام موقتاً در دسترس نیست',
  KYC_AGE_NOT_ELIGIBLE: 'کمتر از ۱۸ سال',
  STALE_BASE: 'نسخه‌ی هویت تغییر کرده است',
  NO_CHANGE: 'تغییری درخواست نشده',
};
/** Human label for a reason code; unknown codes never become the primary label. */
export const reasonLabel = (code: string | null | undefined): string =>
  code ? (REASON_LABEL[code] ?? 'علت ثبت‌شده (جزئیات فنی)') : '—';

/** KYC evidence (kyc.checks) — Persian primary; the raw code stays secondary. */
export const EVIDENCE_TYPE: Record<string, string> = {
  PHONE_POSSESSION: 'تأیید شماره موبایل (کد پیامکی)',
  MOBILE_OWNERSHIP: 'تطبیق مالکیت موبایل با کد ملی',
  CIVIL_IDENTITY: 'استعلام هویت (ثبت احوال)',
  AGE_ELIGIBILITY: 'بررسی سن',
  CARD_OWNERSHIP: 'استعلام مالکیت کارت',
  IBAN_OWNERSHIP: 'استعلام مالکیت شبا',
};
export const OUTCOME: Record<
  string,
  { label: string; tone: 'positive' | 'negative' | 'accent' | 'neutral' }
> = {
  MATCH: { label: 'مطابقت تأیید شد', tone: 'positive' },
  NO_MATCH: { label: 'عدم تطابق', tone: 'negative' },
  AMBIGUOUS: { label: 'نامشخص (حساب مشترک یا چند صاحب)', tone: 'negative' },
  INACTIVE: { label: 'غیرفعال در بانک', tone: 'negative' },
  INVALID: { label: 'شماره‌ی نامعتبر', tone: 'negative' },
  INVALID_INPUT: { label: 'ورودی نامعتبر', tone: 'negative' },
  UNSUPPORTED: { label: 'پشتیبانی‌نشده', tone: 'neutral' },
  PROVIDER_UNAVAILABLE: { label: 'سرویس استعلام موقتاً در دسترس نیست', tone: 'accent' },
  RATE_LIMITED: { label: 'محدودیت تعداد درخواست سرویس', tone: 'accent' },
  PROVIDER_ERROR: { label: 'خطای سرویس استعلام', tone: 'accent' },
};
export const outcomeLabel = (o: string) =>
  OUTCOME[o] ?? { label: 'نتیجه‌ی ثبت‌شده', tone: 'neutral' as const };

export const CORRECTION_STATUS: Record<string, string> = {
  REQUESTED: 'ثبت شده',
  VERIFYING: 'در حال استعلام',
  NEEDS_INFORMATION: 'نیازمند اطلاعات',
  TEMPORARILY_UNAVAILABLE: 'سرویس موقتاً در دسترس نیست',
  VERIFIED_PENDING_APPLY: 'تأییدشده، در انتظار اعمال',
  COMPLETED: 'انجام شد',
  REJECTED: 'رد شد',
  CANCELLED: 'لغو شد',
};

export const TARGET: Record<string, string> = {
  user: 'پرونده‌ی کاربر',
  admin: 'حساب مدیر',
  bank_instrument: 'کارت / شبا',
  session: 'نشست',
  system: 'سامانه',
};

/** OPEN-13: no approved bank name/logo dataset yet — never present a raw code as the bank name. */
export const BANK_NAME_UNAVAILABLE = 'نام بانک در دسترس نیست';

const AUDIT: Record<string, string> = {
  'admin.login': 'ورود مدیر',
  'admin.login_failed': 'ورود ناموفق مدیر',
  'admin.login_recovery_code': 'ورود مدیر با کد بازیابی',
  'admin.logout': 'خروج مدیر',
  'admin.step_up': 'تأیید مجدد هویت مدیر',
  'admin.totp_failed': 'کد دومرحله‌ای نادرست مدیر',
  'admin.totp_reenrolled': 'راه‌اندازی دوباره‌ی دومرحله‌ای مدیر',
  'admin.setup_completed': 'راه‌اندازی حساب مدیر',
  'admin.bootstrap': 'ایجاد حساب مدیر',
  'admin.break_glass_reset': 'بازنشانی اضطراری مدیر',
  'admin.user_search': 'جستجوی کاربر',
  'admin.user_viewed': 'مشاهده‌ی پرونده‌ی کاربر',
  'identity.registered': 'ثبت‌نام کاربر',
  'identity.logout': 'خروج کاربر',
  'identity.password_changed': 'تغییر رمز عبور',
  'identity.password_reset': 'بازیابی رمز عبور',
  'identity.phone_changed': 'تغییر شماره‌ی موبایل',
  'identity.sessions_revoked': 'خروج از دستگاه‌ها',
  'identity.totp_enabled': 'فعال‌سازی ورود دومرحله‌ای',
  'identity.totp_disabled': 'غیرفعال‌سازی ورود دومرحله‌ای',
  'identity.correction_requested': 'درخواست اصلاح هویت',
  'identity.correction_completed': 'تکمیل اصلاح هویت',
  'identity.national_id_change_refused': 'رد تغییر کد ملی',
  'identity.ha_recovery_started': 'شروع بازیابی امنیتی',
  'identity.ha_recovery_completed': 'تکمیل بازیابی امنیتی',
  'kyc1.submitted': 'ثبت احراز هویت',
  'kyc1.result': 'نتیجه‌ی احراز هویت',
  'bank.ownership_check': 'استعلام مالکیت کارت/شبا',
  'bank.deactivated': 'غیرفعال‌سازی کارت/شبا',
  // OD-41 privileged reveal (the value itself is never recorded).
  ADMIN_SENSITIVE_IDENTITY_VIEWED: 'مشاهده‌ی اطلاعات کامل هویتی',
  ADMIN_FULL_PHONE_REVEALED: 'نمایش موبایل کامل',
  ADMIN_FULL_NATIONAL_ID_REVEALED: 'نمایش کد ملی کامل',
  ADMIN_BANK_INSTRUMENT_REVEALED: 'نمایش شماره‌ی کامل کارت/شبا',
  ADMIN_KYC_DOCUMENT_VIEWED: 'مشاهده‌ی مدرک احراز هویت',
  ADMIN_KYC_DOCUMENT_DOWNLOADED: 'دریافت مدرک احراز هویت',
  ADMIN_KYC1_APPROVED: 'تأیید دستی KYC1',
  ADMIN_KYC1_REJECTED: 'رد KYC1 توسط مدیر',
  ADMIN_KYC1_RETURNED_FOR_CORRECTION: 'بازگرداندن KYC1 برای اصلاح',
  ADMIN_KYC1_REVOKED: 'حذف احراز KYC1',
  ADMIN_KYC2_APPROVED: 'تأیید دستی KYC2',
  ADMIN_KYC2_REJECTED: 'رد KYC2 توسط مدیر',
  ADMIN_KYC2_RETURNED_FOR_CORRECTION: 'بازگرداندن KYC2 برای اصلاح',
  ADMIN_KYC2_REVOKED: 'حذف احراز KYC2',
  ADMIN_SET_LEVEL_0: 'بازنشانی کامل احراز',
  ADMIN_SET_LEVEL_1: 'تعیین سطح ۱ توسط مدیر',
  ADMIN_SET_LEVEL_2: 'ارتقای مستقیم به سطح ۲',
  ADMIN_KYC_OVERRIDE: 'تغییر احراز توسط مدیر',
  ADMIN_IDENTITY_CORRECTED: 'اصلاح هویت توسط مدیر',
  ADMIN_NATIONAL_ID_CORRECTED: 'اصلاح کد ملی توسط مدیر',
  ADMIN_PHONE_CHANGED: 'تغییر موبایل توسط مدیر',
  ADMIN_PHONE_REVERIFY: 'تغییر موبایل با الزام احراز دوباره',
  ADMIN_TOTP_DISABLED: 'غیرفعال‌کردن ورود دومرحله‌ای',
  ADMIN_TOTP_RESET: 'بازنشانی ورود دومرحله‌ای',
  ADMIN_TOTP_ENROLLMENT_REQUIRED: 'الزام راه‌اندازی ورود دومرحله‌ای',
  ADMIN_SESSION_REVOKED: 'خروج اجباری یک دستگاه',
  ADMIN_SESSIONS_REVOKED: 'خروج اجباری از همه‌ی دستگاه‌ها',
  ADMIN_REVOKED_ALL: 'خروج اجباری از همه‌ی دستگاه‌ها',
  ADMIN_ACCOUNT_LOCKED: 'قفل حساب',
  ADMIN_ACCOUNT_UNLOCKED: 'رفع قفل حساب',
  ADMIN_ACCOUNT_SUSPENDED: 'تعلیق حساب',
  ADMIN_ACCOUNT_STATUS_CHANGED: 'تغییر وضعیت حساب',
  ADMIN_BANK_VERIFY: 'تأیید دستی مالکیت کارت/شبا',
  ADMIN_BANK_REJECT: 'رد مالکیت کارت/شبا',
  ADMIN_BANK_DEACTIVATE: 'غیرفعال‌سازی کارت/شبا',
  ADMIN_BANK_REACTIVATE: 'فعال‌سازی دوباره‌ی کارت/شبا',
  // Phase 4 money operations (amounts/references live in the audit before/after, never PII).
  ADMIN_LEDGER_CREDIT: 'افزایش موجودی (اصلاحیه)',
  ADMIN_LEDGER_DEBIT: 'کاهش موجودی (اصلاحیه)',
  ADMIN_LEDGER_RESERVE: 'مسدودسازی موجودی',
  ADMIN_LEDGER_RELEASE: 'آزادسازی مسدودی',
  ADMIN_LEDGER_REVERSAL: 'برگشت اصلاحیه',
  TOMAN_DEPOSIT_SUBMITTED: 'ثبت درخواست واریز تومان',
  TOMAN_DEPOSIT_CANCELLED: 'لغو درخواست واریز تومان',
  TOMAN_DEPOSIT_AUTO_CREDITED: 'واریز خودکار پس از تطبیق دقیق',
  ADMIN_TOMAN_DEPOSIT_CREDITED: 'واریز با تصمیم مدیر (پرونده‌ی تطبیق)',
  ADMIN_TOMAN_DEPOSIT_REJECTED: 'رد درخواست واریز',
  BANK_EVIDENCE_RECEIVED: 'دریافت شاهد بانکی',
  ADMIN_BANK_EVIDENCE_RECORDED: 'ثبت شاهد بانکی توسط مدیر',
  ADMIN_BANK_EVIDENCE_RETRY_MATCH: 'اجرای دوباره‌ی تطبیق شاهد بانکی',
  ADMIN_RECONCILIATION_CASE_RESOLVED: 'حل پرونده‌ی تطبیق',
  TOMAN_WITHDRAWAL_REQUESTED: 'درخواست برداشت تومان',
  TOMAN_WITHDRAWAL_CONFIRMED: 'تأیید برداشت تومان با کد پیامکی',
  ADMIN_TOMAN_WITHDRAWAL_COMPLETE: 'ثبت انجام برداشت',
  ADMIN_TOMAN_WITHDRAWAL_REJECT: 'رد برداشت و بازگشت مبلغ',
  ADMIN_TOMAN_WITHDRAWAL_HOLD: 'توقف امنیتی برداشت',
  ADMIN_TOMAN_WITHDRAWAL_RESUME: 'ادامه‌ی برداشت',
  ADMIN_TOMAN_WITHDRAWAL_MARK_UNCERTAIN: 'تسویه‌ی نامشخص برداشت',
  ADMIN_HOPEX_BANK_ACCOUNT_CREATED: 'افزودن حساب بانکی هوپکس',
  ADMIN_HOPEX_BANK_ACCOUNT_STATUS: 'تغییر وضعیت حساب بانکی هوپکس',
  FINANCIAL_MUTATIONS_HALTED: 'توقف خودکار عملیات مالی (مغایرت)',
  ADMIN_FINANCIAL_HALT: 'توقف اضطراری عملیات مالی',
  ADMIN_FINANCIAL_RELEASE: 'رفع توقف عملیات مالی',
  KYC2_SUBMITTED: 'ارسال مدارک احراز سطح ۲',
};

/** Unknown codes get a neutral Persian label; the raw code is shown as secondary metadata. */
export const auditLabel = (code: string): string => AUDIT[code] ?? 'رویداد ثبت‌شده';

const EVENTS: Record<string, string> = {
  REGISTERED: 'ساخت حساب',
  LOGIN_SUCCESS: 'ورود موفق',
  LOGIN_FAILED: 'ورود ناموفق',
  NEW_DEVICE_LOGIN: 'ورود از دستگاه جدید',
  PASSWORD_CHANGED: 'تغییر رمز عبور',
  PASSWORD_RESET: 'بازیابی رمز عبور',
  TOTP_ENABLED: 'فعال شدن ورود دومرحله‌ای',
  TOTP_DISABLED: 'غیرفعال شدن ورود دومرحله‌ای',
  RECOVERY_CODE_USED: 'استفاده از کد بازیابی',
  RECOVERY_CODES_REGENERATED: 'ساخت کدهای بازیابی جدید',
  SESSION_REVOKED: 'خروج یک دستگاه',
  OTHER_SESSIONS_REVOKED: 'خروج دستگاه‌های دیگر',
  PHONE_CHANGED: 'تغییر شماره‌ی موبایل',
  KYC1_SUBMITTED: 'ارسال احراز هویت',
  KYC1_VERIFIED: 'تکمیل احراز هویت',
  BANK_INSTRUMENT_VERIFIED: 'تأیید مالکیت کارت/شبا',
  SUSPICIOUS_RECOVERY: 'بازیابی مشکوک',
  IDENTITY_CORRECTION_REQUESTED: 'درخواست اصلاح هویت',
  IDENTITY_CORRECTED: 'اصلاح هویت',
  ADMIN_KYC_CHANGED: 'تغییر احراز توسط مدیر',
  ADMIN_IDENTITY_CORRECTED: 'اصلاح هویت توسط مدیر',
  ADMIN_PHONE_CHANGED: 'تغییر موبایل توسط مدیر',
  ADMIN_TOTP_DISABLED: 'غیرفعال‌کردن دومرحله‌ای توسط مدیر',
  ADMIN_TOTP_RESET: 'بازنشانی دومرحله‌ای توسط مدیر',
  ADMIN_TOTP_ENROLLMENT_REQUIRED: 'الزام دومرحله‌ای توسط مدیر',
  ADMIN_SESSIONS_REVOKED: 'خروج اجباری توسط مدیر',
  ADMIN_ACCOUNT_STATUS_CHANGED: 'تغییر وضعیت حساب توسط مدیر',
};
export const eventLabel = (code: string): string => EVENTS[code] ?? 'رویداد امنیتی ثبت‌شده';

export const SEVERITY: Record<string, { label: string; tone: 'neutral' | 'accent' | 'negative' }> =
  {
    INFO: { label: 'عادی', tone: 'neutral' },
    WARNING: { label: 'هشدار', tone: 'accent' },
    CRITICAL: { label: 'بحرانی', tone: 'negative' },
  };

export const ACTOR = (t: string): string =>
  t === 'admin' ? 'مدیر' : t === 'user' ? 'کاربر' : t === 'system' ? 'سیستم' : t;

export const CHECK_LABEL: Record<string, string> = {
  phonePossession: 'مالکیت سیم‌کارت (کد پیامکی)',
  mobileOwnership: 'تطابق موبایل با کد ملی',
  civilIdentity: 'تطابق نام و تاریخ تولد با ثبت احوال',
  ageEligibility: 'سن ۱۸ سال تمام (سیاست هوپکس)',
};
export const CHECK_STATE: Record<
  string,
  { label: string; tone: 'positive' | 'negative' | 'accent' | 'neutral' }
> = {
  PASSED: { label: 'تأیید', tone: 'positive' },
  FAILED: { label: 'عدم تطابق', tone: 'negative' },
  UNAVAILABLE: { label: 'سرویس در دسترس نبود', tone: 'accent' },
  NOT_RUN: { label: 'انجام نشده', tone: 'neutral' },
};

export const OWNERSHIP: Record<string, string> = {
  VERIFIED: 'مالکیت تأییدشده',
  PENDING: 'در انتظار استعلام',
  NO_MATCH: 'عدم تطابق',
  AMBIGUOUS: 'نامشخص (مشترک)',
  INACTIVE: 'غیرفعال در بانک',
  UNAVAILABLE: 'سرویس در دسترس نبود',
  REJECTED: 'ردشده',
  INVALID: 'شماره‌ی نامعتبر',
  UNSUPPORTED: 'پشتیبانی‌نشده',
  RECHECK_REQUIRED: 'نیازمند استعلام دوباره (کد ملی تغییر کرده)',
};
